import type { Notifications } from 'types/api-responses';

// Vesting Documents — the recorded document that created a CRM tract, and the deal it came
// through. One row reads: tract T was created through opportunity O by document D. A tract can
// legitimately hold more than one row (one per deal), so the block renders a list.
//
// Backend: Crm::Tracts::VestingDocumentsController and app/views/crm/tracts/vesting_documents/.

/** Placeholder shown wherever a vesting value is empty or missing. */
export const EMPTY = '—';

/** What the search needs from the tract: a date to rank around, an area to scope to. */
export interface VestingContext {
  acquisitionEffectiveDate?: string | null;
  referenceLocationId?: number;
  referenceLocationName?: string | null;
  /** The deal's acquiring entity: pre-fills the search to deeds granted to it. */
  buyerEntity?: { id: number; name: string } | null;
  /** Scopes the search when the tract has no survey area to scope to. */
  county?: { id: number; name: string } | null;
}

/** How the link came to exist. `auto` is the nightly matcher's guess; `manual` is a person's. */
export type VestingProvenance = 'auto' | 'manual';

/**
 * How far the linked document has got through extraction.
 *
 * A freshly uploaded deed is provisioned as a stub with no county, recording reference, or date
 * until extraction lands minutes later — `processing` exists so the block does not render that as
 * a verified answer with an empty citation. `failed` means the reference will never arrive.
 */
export type VestingDocumentState = 'processing' | 'failed' | 'ready';

/** The routes the matcher can find a document by. Mirrors Finder::EVIDENCE_SCORES. */
export type VestingEvidence = 'entity_grantee' | 'grantee_name';

/**
 * The matcher's evidence for a link. ONE shape on the backend (Finder#reason_for), which reaches
 * the frontend in TWO spellings, and the difference is deliberate:
 *
 * - on a Row it is `matchDetail`, a stored snapshot passed to Jbuilder WHOLE — and
 *   `key_format(camelize: :lower)` does not recurse into a hash, so its keys stay snake_case.
 * - on a Candidate it is `reason`, emitted field by field, so its keys ARE camelCase.
 *
 * The two look contradictory and are not. Keep them as separate types so neither side can read
 * the other's spelling and silently get `undefined`.
 */
export interface VestingMatchDetail {
  evidence?: VestingEvidence[];
  document_type?: string | null;
  date?: string | null;
  date_source?: string | null;
  interest_type_match?: boolean;
  score?: number;
}

/** The camelCase twin of {@link VestingMatchDetail}, carried on a candidate. */
export interface VestingCandidateReason {
  evidence?: VestingEvidence[];
  documentType?: string | null;
  date?: string | null;
  dateSource?: string | null;
  interestTypeMatch?: boolean;
  score?: number;
}

/**
 * The lean document block a Row carries.
 *
 * Deliberately NOT the canonical documents/_document partial, which renders the whole citation
 * graph. Do not reach for more fields here; add them to the backend partial if the block
 * genuinely needs them.
 */
export interface VestingDocumentDetail {
  id: number;
  /** The document's own subtype route, e.g. `/courthouse_documents/:id`. */
  href: string;
  /** Recording vocabulary ('Warranty Deed'), NOT the STI class and NOT the provisioning selector. */
  documentType: string | null;
  volume: string | null;
  page: string | null;
  instrumentNumber: string | null;
  recordedDate: string | null;
  county: string | null;
  /**
   * The document's own Pusher block. `extraction_status_change` lands here when a provisioned
   * deed finishes being read, which is the only way the block learns its citation arrived —
   * the vesting controller itself broadcasts nothing.
   */
  notifications?: Notifications;
}

/** One vesting document link on a tract. */
export interface VestingDocumentRow {
  id: number;
  /** This row's own path: `/crm/tracts/:tract_id/vesting_documents/:id`. */
  href: string;
  provenance: VestingProvenance;
  verified: boolean;
  verifiedAt: string | null;
  verifiedBy: string | null;
  createdAt: string | null;
  createdBy: string | null;
  documentState: VestingDocumentState;
  /** Empty for a manual row — a person chose it, so there is no evidence to show. */
  matchDetail: VestingMatchDetail;
  opportunity: {
    id: string;
    name: string;
    salesforceHref: string;
  } | null;
  document: VestingDocumentDetail;
}

/**
 * A document as the corpus search (`/queries/documents`) names and shapes it. A candidate's
 * document comes in the same shape, so the catalog reads both alike.
 */
export type CatalogDocument = {
  id: number;
  href: string;
  documentType: string | null;
  volume?: string | null;
  page?: string | null;
  instrumentNumber?: string | null;
  recordedDate?: string | null;
  instrumentDate?: string | null;
  fileDate?: string | null;
  documentSubtype?: string | null;
  stateAbbreviation?: string | null;
  county?: { name: string } | null;
  partySummary?: {
    grantors?: { name: string }[];
    grantees?: { name: string }[];
  };
};

/** A document the finder thinks could be the vesting document, with the evidence for it. */
export interface VestingCandidate {
  documentId: number;
  /** Also present inside `reason`; the backend emits it in both places. */
  score: number | null;
  reason: VestingCandidateReason;
  document: CatalogDocument;
}

/** GET {basePath}/vesting_documents */
export interface VestingDocumentsListResponse {
  vestingDocuments: VestingDocumentRow[];
}

/** GET {basePath}/vesting_documents/candidates */
export interface VestingCandidatesResponse {
  candidates: VestingCandidate[];
}

export const PROVENANCE_LABELS: Record<VestingProvenance, string> = {
  auto: 'Auto-Connected',
  manual: 'Linked manually',
};

// What each evidence route actually proves, in a landman's terms rather than the finder's.
export const EVIDENCE_LABELS: Record<VestingEvidence, string> = {
  entity_grantee: 'Grantee is a title entity on this tract',
  grantee_name: 'Grantee name matches the acquiring account',
};

// What a document is called when it has no recording vocabulary of its own — a deed provisioned
// minutes ago has none until extraction lands.
export const documentHeading = (documentType?: string | null): string =>
  documentType?.trim() || 'Recorded document';

// The recording citation as one line: "Vol 123, Pg 45" or "Inst. 2019-0042". Volume and page win:
// a Texas deed is cited by book and page, and the instrument number is the fallback for counties
// that file by instrument — many deeds carry both.
//
// Null rather than a placeholder when the document carries neither, which is the normal state of
// a deed still being extracted — the caller decides whether that reads as EMPTY or as "still
// processing", and those are different things to a user.
//
// Takes the three fields rather than a document because the corpus search returns its own row
// shape; one rule, whatever carries it.
export const recordingReference = (
  volume?: string | null,
  page?: string | null,
  instrumentNumber?: string | null
): string | null => {
  if (volume && page) return `Vol ${volume}, Pg ${page}`;
  if (instrumentNumber) return `Inst. ${instrumentNumber}`;
  return null;
};

// Type, citation and county: what a person reads to know which deed a row or chip means.
export const vestingDocumentLabel = ({
  document,
}: Pick<VestingDocumentRow, 'document'>): string =>
  [
    documentHeading(document.documentType),
    recordingReference(
      document.volume,
      document.page,
      document.instrumentNumber
    ),
    document.county,
  ]
    .filter(Boolean)
    .join(' · ');

// The matcher's evidence as readable lines.
//
// Takes the two fields rather than the object: they arrive snake_case on a Row's `matchDetail`
// and camelCase on a candidate's `reason`, so each caller names its own spelling once, at the
// call site, and nothing downstream has to know which shape it holds.
export const evidenceLabels = (
  evidence?: VestingEvidence[],
  interestTypeMatch?: boolean
): string[] => {
  const labels = (evidence ?? []).map(
    (route) => EVIDENCE_LABELS[route] ?? route
  );
  if (interestTypeMatch) labels.push('Interest type agrees with the tract');
  return labels;
};

// A row awaits review when the matcher proposed it and nobody has verified it yet.
export const awaitsReview = (
  row: Pick<VestingDocumentRow, 'provenance' | 'verified'>
): boolean => row.provenance === 'auto' && !row.verified;
